import { Button } from "@solomon-labs/design/components/ui";
import React from "react";

import { cn } from "../../utils/cn";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from "../ui/collapsible";
import { textVariants } from "../ui/text";

interface PageErrorFallbackProps {
    error?: Error;
    retry: () => void;
}

export function PageErrorFallback({
    error,
    retry
}: PageErrorFallbackProps): React.ReactElement {
    return (
        <div className="flex flex-1 flex-col items-center justify-center md:justify-start md:pt-[12vh] px-6">
            <div className="flex flex-col items-center text-center max-w-[420px]">
                {/* Animated exclamation mark — decorative display element,
                    not text content. text-[8rem]/[12rem] is well beyond any
                    Solomon Text variant; font-numeric-mono (APK Narrative
                    Mono) is intentional and not part of the typography
                    ladder. Treat as a glyph. */}
                <span className="font-numeric-mono font-bold tracking-tight text-foreground-base leading-none text-[8rem] md:text-[12rem] animate-not-found-hero">
                    !
                </span>

                <div className="w-16 md:w-24 h-[2px] bg-surface-inverse-raised mt-4 animate-not-found-divider" />

                <h1
                    className={cn(
                        textVariants({ variant: "h1" }),
                        "mt-4 text-foreground-base animate-not-found-heading"
                    )}
                >
                    Something went wrong
                </h1>

                <p
                    className={cn(
                        textVariants({ variant: "body" }),
                        "mt-1 text-foreground-neutral max-w-[32ch] animate-not-found-description"
                    )}
                >
                    We hit an unexpected issue loading this page. Try
                    refreshing, or head back home.
                </p>

                <div className="flex gap-3 mt-8 animate-not-found-cta">
                    <Button
                        variant="raised"
                        size="sm"
                        interaction="lifted"
                        onClick={retry}
                    >
                        Try again
                    </Button>
                    <Button
                        variant="transparent"
                        size="sm"
                        interaction="lifted"
                        outline
                        onClick={() => {
                            window.location.href = "/";
                        }}
                    >
                        Go home
                    </Button>
                </div>

                {error?.message && (
                    <Collapsible className="mt-8 w-full animate-not-found-cta group/err-details">
                        <CollapsibleTrigger
                            render={
                                <Button
                                    variant="transparent"
                                    interaction="default"
                                    size="xs"
                                    className="mx-auto"
                                    leading={
                                        <span className="inline-block transition-transform duration-200 group-data-[panel-open]/err-details:rotate-90">
                                            &#9656;
                                        </span>
                                    }
                                />
                            }
                        >
                            Error details
                        </CollapsibleTrigger>
                        <CollapsibleContent className="data-closed:hidden">
                            <pre className="mt-2 text-sm text-foreground-neutral bg-surface-sunken rounded-3xl p-3 text-left overflow-x-auto max-h-[200px] overflow-y-auto break-words whitespace-pre-wrap">
                                {error.message}
                            </pre>
                        </CollapsibleContent>
                    </Collapsible>
                )}
            </div>
        </div>
    );
}

export type { PageErrorFallbackProps };
